import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth } from '@auth/AuthProvider';
import ServicesController from '@data/controller/ServicesController';
import { ChurchService } from '@interfaces/service.interface';

const HTTP_NOT_FOUND = 404;

// One service by ID. Pass null to hold off, as when the service is already in
// hand. `notFound` separates a service that isn't there from a failed request.
export default function useService(serviceID: string | null) {
  const auth = useAuth();
  const churchID = auth.churchId;

  const [service, setService] = useState<ChurchService | null>(null);
  const [loading, setLoading] = useState<boolean>(serviceID !== null);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [notFound, setNotFound] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Only the latest request's answer is kept, if the ID changes mid-flight.
  const latestRequest = useRef(0);

  const fetch = useCallback(async () => {
    if (!churchID || !serviceID) return;

    const request = ++latestRequest.current;

    setLoading(true);
    setError(null);
    setNotFound(false);

    const response = await ServicesController.fetchService(churchID, serviceID, auth);

    if (request !== latestRequest.current) return;

    if (response.status === HTTP_NOT_FOUND) {
      setService(null);
      setNotFound(true);
    } else if (response.error || !response.body) {
      setService(null);
      setError(response.error || 'Failed to load this service.');
    } else {
      setService(response.body);
    }

    setLoading(false);
    setHasLoaded(true);
  }, [churchID, serviceID, auth]);

  useEffect(() => {
    latestRequest.current++;
    setService(null);
    setHasLoaded(false);
    setNotFound(false);
    setError(null);

    if (!serviceID) {
      setLoading(false);
      return;
    }

    void fetch();
  }, [serviceID, fetch]);

  return { service, loading, hasLoaded, notFound, error, refetch: fetch } as const;
}
